import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AdvertisementService } from './advertisement.service';
import { AuthenticationService } from './authentication.service';
import { ProductService } from './product.service';
import { BundleService } from './bundle.service';
import { PromosService } from './promos.service';
import { NotificationService } from '../notification/notification.service';
import { NotificationType } from '../enum/notification-type.enum';
import { AnnouncementPreviewDialogComponent } from '../announcement-preview-dialog/announcement-preview-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class AdvertisementFlowService {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private advertisementService = inject(AdvertisementService);
  private authenticationService = inject(AuthenticationService);
  private productService = inject(ProductService);
  private bundleService = inject(BundleService);
  private promosService = inject(PromosService);
  private notificationService = inject(NotificationService);

  private readonly SEEN_ADS_STORAGE_KEY = 'seen_advertisements';

  public getSeenAdvertisementIds(): string[] {
    try {
      const raw = localStorage.getItem(this.SEEN_ADS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  public markAdvertisementAsSeen(adId: string): void {
    const seenIds = this.getSeenAdvertisementIds();
    if (!seenIds.includes(adId)) {
      seenIds.push(adId);
      localStorage.setItem(this.SEEN_ADS_STORAGE_KEY, JSON.stringify(seenIds));
    }
  }

  public checkAndShowAdvertisements(cityId?: string | null, isDelivery: boolean = false): void {
    const token = this.authenticationService.getToken() || '';
    const body = isDelivery && cityId ? { deliveryCity: cityId } : {};

    this.advertisementService.getAdvertisement(token, undefined, undefined, body).subscribe({
      next: (res: any) => {
        const ads = res.body?.data || [];
        const seenIds = this.getSeenAdvertisementIds();

        const queue = ads.filter((ad: any) =>
          !ad.config?.settings?.showOnce || !seenIds.includes(ad.id)
        );

        this.showNextAdvertisement(queue);
      },
      error: () => {
        // Silencieux
      }
    });
  }

  private showNextAdvertisement(queue: any[]): void {
    if (!queue.length) return;

    const ad = queue.shift();
    const delay = ad.config?.settings?.delayMs ?? 0;

    setTimeout(() => {
      const dialogRef = this.dialog.open(AnnouncementPreviewDialogComponent, {
        panelClass: 'custom-preview-dialog-container',
        maxWidth: '90vw',
        data: {
          id: ad.id,
          templateId: ad.config?.templateId,
          themeId: ad.config?.themeId,
          title: ad.config?.title,
          body: ad.config?.body,
          image: ad.config?.image,
          primaryAction: ad.config?.primaryAction,
          secondaryAction: ad.config?.secondaryAction,
          settings: ad.config?.settings
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result && ad.config?.settings?.showOnce) {
          this.markAdvertisementAsSeen(ad.id);
        }

        if (result?.action === 'primary') {
          this.handleAdvertisementAction(
            result.type,
            result.targetId,
            result.targetName || ad.config?.primaryAction?.targetName
          );
        }

        this.showNextAdvertisement(queue);
      });
    }, delay);
  }

  public handleAdvertisementAction(type: string, targetId: string, targetName?: string): void {
    if (type === 'product' && targetId) {
      this.navigateToProductDetails(targetId);
    } else if (type === 'bundle' && (targetName || targetId)) {
      this.navigateToBundleDetails(targetName || targetId);
    } else if (type === 'promotion' && (targetName || targetId)) {
      this.navigateToPromotionDetails(targetName || targetId);
    } else {
      this.router.navigate(['/product']);
    }
  }
  private navigateToBundleDetails(bundleSlug: string): void {
    this.bundleService.getBundleBySlug(bundleSlug).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const bundle = res?.body?.data;
      if (!bundle) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          "Ce menu n'est plus disponible pour le moment",
          ''
        );
        return;
      }

      this.router.navigate(['/menus', bundle.slug || bundleSlug], {
        state: {
          bundleId: bundle.id,
          bundleSlug: bundle.slug || bundleSlug,
          returnToCommand: false,
          fromMenu: false
        }
      });
    });
  }

  private navigateToProductDetails(productId: string): void {
    this.productService.getProductById(productId).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const product = res?.body?.data;
      if (!product) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          'Le produit n\'est pas disponible pour le moment',
          ''
        );
        return;
      }

      const catSlug = product.category?.slug || product.categoryId;

      this.router.navigate(['/product', catSlug, product.slug], {
        queryParams: { size: null },
        state: {
          returnToCommand: false,
          slug: product.slug,
          editingCartItemId: false,
          selectedSize: null,
          selectedPrice: null,
          discountReduction: null,
          discountActivatedFor: [],
          discountOrderTypes: [],
          discountPromotionId: null
        }
      });
    });
  }

  private navigateToPromotionDetails(promoSlug: string): void {
    this.promosService.getPromotionBySlug(promoSlug).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const promo = res?.body?.data || res?.body;
      if (!promo) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          "La promotion n'est plus disponible pour le moment",
          ''
        );
        return;
      }

      this.router.navigate(['/nos-promos', promo.slug || promoSlug], {
        state: {
          returnToCommand: false,
          promoSlug: promo.slug || promoSlug
        }
      });
    });
  }
}
